import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// Timed transcript segments of a visit (v3.2 trust layer): about one sentence
// or utterance of one speaker with its position in the recording, for
// per-line citations, replaying a moment and flagging a segment.
//
// One entry per transcript: the visit's own (recordingPublicId null) and each
// added recording's, in recording order. Segments exist only where the
// server's transcription returned timings (batch Deepgram, ElevenLabs,
// single-request Whisper) and the stored transcript still has the words they
// were made from. Clients show the plain transcript otherwise.
export const TRANSCRIPT_SEGMENT_SOURCES = [
  "deepgram",
  "elevenlabs",
  "openai",
  // Reserved: realtime transcription and on-device (iOS) timings.
  "live",
  "device",
] as const;
export const TRANSCRIPT_SEGMENT_STATES = [
  // Segments match the stored transcript.
  "ready",
  // No timings for this transcript (legacy path, provider without timings,
  // translated transcript, still transcribing).
  "none",
  // The transcript changed after its timings were stored (edited, rewritten
  // by another writer); the old timings are withheld.
  "stale",
] as const;
// Segments returned per visit; a visit over this is cut and `truncated` set.
export const TRANSCRIPT_SEGMENTS_MAX = 20_000;
export const transcriptSegmentSchema = z.object({
  // 0-based order within its transcript.
  idx: z.int().min(0),
  startMs: z.int().min(0),
  endMs: z.int().min(0),
  // Diarized speaker number ("Speaker 1:" in the transcript); roles come from
  // /speakers.
  speaker: z.int().min(0).max(99).nullable(),
  text: z.string(),
});
export type TranscriptSegmentItem = z.output<typeof transcriptSegmentSchema>;
export const visitTranscriptSegmentsSchema = z.object({
  transcripts: z.array(
    z.object({
      recordingPublicId: publicIdSchema.nullable(),
      state: z.enum(TRANSCRIPT_SEGMENT_STATES),
      source: z.enum(TRANSCRIPT_SEGMENT_SOURCES).nullable(),
      // The recording's audio, for replay with Range requests (same
      // authentication). Null when no audio is stored.
      audio: z.object({
        available: z.boolean(),
        url: z.string().startsWith("/api/v2/visits/").nullable(),
      }),
      segments: z.array(transcriptSegmentSchema),
    }),
  ),
  truncated: z.boolean(),
});
export type VisitTranscriptSegments = z.output<
  typeof visitTranscriptSegmentsSchema
>;
export const transcriptSegmentsContract = {
  getVisitTranscriptSegments: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Transcript segments unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query: z.strictObject({}).default({}),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/visits/{public_id}/transcript-segments",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitTranscriptSegmentsSchema),
};
